// All widget CSS, scoped to the shadow root. Injected as a single <style> so the
// host page's CSS can't reach in and ours can't leak out. Modelled on the
// intervention-client C&I embed: a floating bottom-right card (not a drawer),
// centered header, segmented pill tabs, "Ask anything…" search, bordered
// list-cards, folder cards + collapsible sections. Accent is brand-driven
// (Validaide teal by default; SkyCell blue via data-brand="skycell").

import { KB_BRANDS, type KbBrand } from "@skycell-ag/kb-core";

/** Build the shadow-root CSS for a given brand. */
export function widgetCss(brand: KbBrand): string {
  const b = KB_BRANDS[brand];
  return `
:host, * { box-sizing: border-box; }

/* Icons are decoration, never the click target.
   Every control in here is an icon inside a button (or a link). SVG defaults to
   pointer-events: visiblePainted, so a press on a PAINTED part of the glyph
   targets the <path>, while a press on the empty space around it falls through
   to the button. The two are not equivalent — and a glyph like the collapse
   "minus", a single stroke straight across the middle, is painted exactly where
   people aim. That is what made the collapse button respond near its edges but
   not in its centre.
   Taking the icon out of hit-testing makes the whole 32x32 button one target,
   and it also keeps isDragHandle() (drag.ts) simple: the header's pointerdown
   target is then always the button, never a path inside it. */
.kb-scope button svg,
.kb-scope a svg { pointer-events: none; }
:host {
  --kb-primary: ${b.primary};
  --kb-primary-hover: ${b.primaryHover};
  --kb-primary-ink: #ffffff;
  /* primary at ~10% / ~16% for tinted pills + answer bubbles */
  --kb-primary-tint: color-mix(in srgb, ${b.primary} 10%, #ffffff);
  --kb-primary-tint-strong: color-mix(in srgb, ${b.primary} 16%, #ffffff);
  --kb-ink: #0f172a;
  --kb-muted: #475569;
  --kb-faint: #64748b;
  --kb-border: #e2e8f0;
  --kb-border-strong: #cbd5e1;
  --kb-bg: #ffffff;
  --kb-bg-soft: #f8fafc;
  --kb-shadow: 0 12px 40px rgba(15, 23, 42, 0.22);
  --kb-radius: 8px;
}

/* Anchor color + font on a wrapper INSIDE the shadow tree so host !important
   rules matching :host can't leak in via inheritance. */
.kb-scope {
  font-family: "Inter", system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  color: var(--kb-ink);
  font-size: 14px;
  line-height: 1.5;
}

/* Launcher — docks to the edge set by data-side (default right; Validaide puts
   it left, where its Help button has always been and clear of WhatFix). */
.kb-launcher {
  position: fixed; bottom: 16px; z-index: 2147483000;
  display: inline-flex; align-items: center; gap: 8px;
  padding: 12px 18px; border: 0; border-radius: 999px; cursor: pointer;
  background: var(--kb-primary); color: var(--kb-primary-ink);
  font-size: 15px; font-weight: 600; line-height: 1;
  box-shadow: var(--kb-shadow);
  transition: transform .12s ease, background .12s ease;
}
.kb-launcher:hover { transform: translateY(-1px); background: var(--kb-primary-hover); }
.kb-launcher svg { width: 18px; height: 18px; }

/* Floating card (non-blocking; no backdrop) */
.kb-card {
  position: fixed; bottom: 16px; z-index: 2147483002;
  width: 420px; height: 660px;
  min-height: 320px;
  max-width: calc(100vw - 32px); max-height: calc(100vh - 32px);
  background: var(--kb-bg); border-radius: var(--kb-radius);
  box-shadow: var(--kb-shadow);
  display: flex; flex-direction: column; overflow: hidden;
  transition: width .2s ease, height .2s ease;
}
.kb-card.expanded {
  width: var(--kb-expanded-width, 1200px); height: calc(100vh - 32px);
  /* The reading measure, and the width media is allowed to break out to:
     the panel minus .kb-body's 24px gutters and .kb-article's 4px padding. */
  --kb-measure: 760px;
  --kb-breakout: calc(min(var(--kb-expanded-width, 1200px), 100vw - 32px) - 56px);
}

/* Dragged (drag.ts): explicit viewport coordinates come in as inline left/top,
   so the corner dock has to let go — otherwise 'right' and 'left' both apply and
   the panel is stretched between them. */
.kb-card.floating { right: auto; bottom: auto; }
/* No size/position easing mid-drag: a transition here makes the panel lag the
   cursor. Selection is suppressed so a drag across the title doesn't highlight it. */
.kb-card.dragging { transition: none; user-select: none; }

/* Collapsed to its header bar — the chat-window minimize. The body and footer
   aren't rendered at all (App.tsx), so the card just shrinks to its header;
   min-height has to be released or the 320px floor would keep it panel-sized. */
.kb-card.collapsed { height: auto; min-height: 0; }
.kb-card.collapsed .kb-header { border-bottom: 0; }
/* The whole bar restores the panel, so it reads as clickable. */
.kb-card.collapsed .kb-header { cursor: pointer; }

.side-right { right: 16px; }
.side-left { left: 16px; }

/* Header */
.kb-header {
  display: flex; align-items: center; gap: 4px;
  padding: 4px 8px; border-bottom: 1px solid var(--kb-border);
  /* Also the drag handle: 'grab' on the bar, 'grabbing' while it is held. */
  cursor: grab;
  touch-action: none; /* let pointermove drive the drag instead of scrolling */
}
.kb-card.dragging .kb-header { cursor: grabbing; }
/* The header's own buttons are clicks, not grabs (see isDragHandle in drag.ts). */
.kb-header button { cursor: pointer; }
.kb-header .kb-title {
  flex: 1; text-align: center; font-size: 16px; font-weight: 500;
  letter-spacing: .25px; color: var(--kb-ink);
}
.kb-header-actions { display: inline-flex; gap: 2px; }
.kb-iconbtn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border: 0; border-radius: 6px; cursor: pointer;
  background: transparent; color: var(--kb-faint);
}
.kb-iconbtn:hover:not(:disabled) { background: var(--kb-bg-soft); color: var(--kb-ink); }
/* The back chevron keeps its slot at the list root (design) but does nothing. */
.kb-iconbtn:disabled { opacity: .35; cursor: default; }
.kb-iconbtn svg { width: 16px; height: 16px; }
/* "Copied" feedback next to the article Share button */
.kb-copied {
  align-self: center; font-size: 12px; font-weight: 500;
  color: var(--kb-primary-hover);
}

/* Segmented pill tabs */
.kb-tabs { display: flex; gap: 8px; padding: 12px 24px 0; }
.kb-tab {
  flex: 1; padding: 8px 12px; border-radius: var(--kb-radius); cursor: pointer;
  font-size: 14px; font-weight: 400; line-height: 1;
  background: var(--kb-bg-soft); border: 1px solid transparent; color: var(--kb-muted);
}
.kb-tab:hover { background: #eef2f7; }
.kb-tab.active {
  background: var(--kb-primary-tint); border-color: var(--kb-primary);
  color: var(--kb-primary-hover); font-weight: 400;
}

/* Search */
.kb-search { display: flex; align-items: flex-start; gap: 8px; padding: 12px 24px 0; }
/* The field grows with the question (textarea, 1 → ~5 lines; see
   MAX_SEARCH_HEIGHT in App.tsx), so long questions wrap instead of scrolling
   sideways. Items stay top-aligned once it is more than one line tall. */
.kb-search-field {
  flex: 1; display: flex; align-items: flex-start; gap: 8px;
  border: 1px solid var(--kb-border-strong); border-radius: 4px;
  padding: 9px 12px; background: var(--kb-bg);
}
.kb-search-field:focus-within { border-color: var(--kb-primary); }
.kb-search-icon { display: inline-flex; color: var(--kb-faint); height: 20px; align-items: center; }
.kb-search-icon svg { width: 16px; height: 16px; }
.kb-search-field textarea {
  flex: 1; border: 0; outline: none; background: transparent; resize: none;
  padding: 0; margin: 0; overflow-y: auto;
  font: inherit; font-size: 14px; line-height: 20px; color: var(--kb-ink);
}
.kb-search-field textarea::placeholder { color: var(--kb-faint); }
.kb-search-clear {
  margin-top: 2px;
  display: inline-flex; align-items: center; justify-content: center;
  width: 20px; height: 20px; border: 0; border-radius: 4px; padding: 0;
  background: transparent; color: var(--kb-faint); cursor: pointer;
}
.kb-search-clear:hover { background: var(--kb-bg-soft); }
.kb-search-clear svg { width: 14px; height: 14px; }
.kb-send {
  display: inline-flex; align-items: center; justify-content: center;
  width: 40px; height: 40px; flex: 0 0 auto;
  border: 0; border-radius: 4px; cursor: pointer;
  background: var(--kb-primary-hover); color: var(--kb-primary-ink);
}
.kb-send:hover:not(:disabled) { background: var(--kb-primary); }
.kb-send:disabled { background: var(--kb-border); color: var(--kb-faint); cursor: default; }
.kb-send svg { width: 18px; height: 18px; }

/* Body */
/* 24px side gutters run through the whole panel — tabs, search, body, footer —
   so the search field, the rows under it and the footer button share one edge
   (VSI-256 QA measured 12px against the design's 24px). */
.kb-body { flex: 1; overflow-y: auto; padding: 24px; }

.kb-status-line {
  color: var(--kb-faint); font-size: 12px; font-weight: 600;
  text-transform: uppercase; letter-spacing: .04em; margin: 0 2px 8px;
}

/* List cards + rows */
.kb-list-card {
  border: 1px solid var(--kb-border); border-radius: var(--kb-radius);
  overflow: hidden; background: var(--kb-bg);
}
.kb-list-stack { display: flex; flex-direction: column; gap: 8px; }
/* Sections inside an open category — the Zendesk section headings. */
.kb-section + .kb-section { margin-top: 20px; }
.kb-section-title { font-size: 14px; font-weight: 600; color: var(--kb-ink); margin: 0 2px 8px; }
.kb-row {
  display: flex; flex-direction: column; gap: 4px;
  width: 100%; text-align: left; border: 0; background: transparent;
  padding: 12px; cursor: pointer;
}
/* Inside a card (a folder's contents) rows are divided; standing free in a
   stack (search results, Relevant) each row is its own card with a gap. */
.kb-list-card > .kb-row:not(:last-of-type) { border-bottom: 1px solid var(--kb-border); }
.kb-list-stack > .kb-row {
  border: 1px solid var(--kb-border); border-radius: var(--kb-radius);
  background: var(--kb-bg);
}
.kb-list-stack > .kb-row:hover { border-color: var(--kb-border-strong); }
.kb-row:hover { background: var(--kb-bg-soft); }
.kb-row-title { font-size: 14px; font-weight: 500; line-height: 18px; color: var(--kb-ink); }
.kb-row-folder {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; color: var(--kb-faint);
}
.kb-row-folder-icon { width: 12px; height: 12px; }

/* Folder tiles (All tab). Narrow, the grid is a single column of rows; in the
   expanded panel .tiles turns it into an even grid — every folder gets the same
   cell, so the row/column rhythm holds however many folders there are and no
   tile reads as more important than its neighbour. */
.kb-folder-grid { display: grid; grid-template-columns: 1fr; gap: 8px; }
.kb-folder-grid.tiles {
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  grid-auto-rows: 112px; gap: 12px;
}
.kb-folder-tile {
  display: flex; align-items: center; gap: 12px;
  width: 100%; text-align: left; cursor: pointer;
  border: 1px solid var(--kb-border); border-radius: var(--kb-radius);
  background: var(--kb-bg); padding: 12px;
}
.kb-folder-tile:hover { background: var(--kb-bg-soft); border-color: var(--kb-border-strong); }
/* In the tile grid a tile fills its cell and stacks its label at the bottom.
   Background is left to .kb-folder-tile — a flat surface, so the tiles read as
   one even field and the :hover state isn't overridden by a gradient. */
.kb-folder-grid.tiles .kb-folder-tile {
  flex-direction: column; align-items: flex-start; justify-content: flex-end;
  gap: 4px; height: 100%; padding: 14px;
}
.kb-folder-grid.tiles .kb-folder-chevron { display: none; }
.kb-folder-glyph { width: 18px; height: 18px; flex: 0 0 auto; color: var(--kb-primary); }
.kb-folder-name { flex: 1; font-size: 14px; font-weight: 500; color: var(--kb-ink); }
.kb-folder-grid.tiles .kb-folder-name { flex: 0 0 auto; }
.kb-folder-meta { font-size: 12px; font-weight: 400; color: var(--kb-faint); }
.kb-folder-chevron { display: inline-flex; color: var(--kb-faint); }
.kb-folder-chevron svg { width: 16px; height: 16px; }

.kb-state { padding: 28px 18px; color: var(--kb-faint); font-size: 14px; text-align: center; }
.kb-error { color: #b42318; }
/* Informational, not an error: the panel is working, just from storage. */
.kb-stale { margin: 0; padding: 8px 14px; font-size: 12.5px; line-height: 1.45;
  color: #7a4b06; background: #fdf6e7; border-bottom: 1px solid #f0e0bd; }
.kb-retry { display: block; margin: 10px auto 0; padding: 6px 14px; font: inherit; font-size: 13px;
  color: var(--kb-primary); background: none; border: 1px solid currentColor; border-radius: 6px; cursor: pointer; }
.kb-retry:hover { background: var(--kb-primary-tint); }

/* AI answer panels (ADR 0005): accent-tinted when the KB answered, grey when it
   couldn't and is falling back to the article list. */
.kb-answer {
  margin: 0 0 12px; padding: 12px 14px; border-radius: var(--kb-radius);
  background: var(--kb-primary-tint); border: 1px solid var(--kb-primary);
  font-size: 14px; line-height: 1.55; color: var(--kb-ink);
}
.kb-answer.none {
  background: var(--kb-bg-soft); border-color: var(--kb-border);
  color: var(--kb-muted);
}
.kb-answer-label {
  display: flex; align-items: center; gap: 6px; margin-bottom: 6px;
  font-size: 12px; font-weight: 600; line-height: 16px; color: var(--kb-primary-hover);
  text-transform: uppercase; letter-spacing: .04em;
}
.kb-answer-label svg { width: 14px; height: 14px; }
.kb-answer-text { white-space: pre-wrap; }
/* Thinking screen: a 312x333 block centred in the body — vertically too, so
   min-height takes the full body and justify-content does the centring. The
   design carries no icon here, only the dots and the label. */
.kb-thinking {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 16px; width: 312px; min-height: max(333px, 100%); max-width: 100%;
  margin: 0 auto; padding: 32px; box-sizing: border-box; text-align: center;
  color: var(--kb-muted); font-size: 14px;
}
.kb-thinking-text { display: inline-flex; align-items: center; gap: 8px; }
/* Three dots, pulsing in sequence while the model is still silent. */
.kb-dots { display: inline-flex; gap: 4px; }
.kb-dots i {
  width: 6px; height: 6px; border-radius: 50%; background: var(--kb-primary);
  animation: kb-blink 1.2s infinite ease-in-out;
}
.kb-dots i:nth-child(2) { animation-delay: .2s; }
.kb-dots i:nth-child(3) { animation-delay: .4s; }
@keyframes kb-blink { 0%, 80%, 100% { opacity: .25; } 40% { opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .kb-dots i { animation: none; } }

/* Empty result (Figma "no-results-state", 312 wide): 32px above and below,
   24px at the sides, 80px icon circle, 24px to the 18px title, 12px to the
   14px line — centred in the body. .centre also centres it vertically, for the
   states (AI no-result, empty search, empty Relevant tab) that own the body. */
.kb-empty {
  display: flex; flex-direction: column; align-items: center; gap: 12px;
  max-width: 312px; margin: 0 auto; padding: 32px 24px; box-sizing: border-box;
  text-align: center;
}
.kb-empty.centre { flex: 1; min-height: 100%; justify-content: center; }
/* Status line on top, empty state centred in what is left of the body. */
.kb-results { display: flex; flex-direction: column; min-height: 100%; }
.kb-empty-glyph {
  display: inline-flex; align-items: center; justify-content: center;
  width: 80px; height: 80px; border-radius: 999px; margin-bottom: 12px;
  background: var(--kb-bg-soft); color: var(--kb-ink);
}
.kb-empty-glyph svg { width: 40px; height: 40px; }
.kb-empty-title { margin: 0; font-size: 18px; font-weight: 500; line-height: 24px; color: var(--kb-ink); }
.kb-empty-text { margin: 0; font-size: 14px; line-height: 20px; color: var(--kb-muted); }

/* Footer — direct line to Customer Support */
.kb-footer {
  border-top: 1px solid var(--kb-border); padding: 10px 24px; background: var(--kb-bg);
  display: flex; flex-direction: column; gap: 10px;
}
/* Line the footer up with the centred article measure instead of running to the
   panel edges, so the bar reads as part of the same column. */
.kb-card.expanded .kb-footer > * { width: 100%; max-width: 760px; margin: 0 auto; }
/* One shape for both footer affordances — the standalone-app link (an <a>) and
   the contact-form trigger (a <button>). The button needs the font and
   background reset an anchor doesn't. */
.kb-support {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; padding: 10px 12px; border-radius: var(--kb-radius);
  border: 1px solid var(--kb-border-strong); background: var(--kb-bg);
  font: inherit; font-size: 14px; font-weight: 500; text-decoration: none;
  color: var(--kb-primary-hover); cursor: pointer;
}
.kb-support:hover { background: var(--kb-primary-tint); border-color: var(--kb-primary); }
.kb-support svg { width: 16px; height: 16px; }
/* Secondary to "Contact support": leaving for the standalone app is a
   convenience, reaching a human is the escalation. */
.kb-standalone { color: var(--kb-muted); }
.kb-standalone:hover { color: var(--kb-primary-hover); }

/* Article view */
.kb-article { padding: 4px 4px 24px; }
/* A wide panel is for browsing the grid, not for 200-character prose lines —
   cap the measure and centre it once there's room. */
.kb-card.expanded .kb-article { max-width: var(--kb-measure); margin: 0 auto; }
/* Prose stays at the measure, but a screenshot or a table squeezed into 760px
   is the reason the panel was expanded in the first place. Media breaks out of
   the column to the full panel width: margin-left: 50% puts its left edge on
   the column's centre line, and the translate pulls it back by half its own
   width, so it stays centred on that axis whatever it ends up measuring.
   Images are gated on .kb-zoomable (App.tsx; natural area >= 200x200), which
   is already the widget's "this is a figure, not an icon" test — inline icons
   keep their natural size and their place in the text. */
.kb-card.expanded .kb-content > :is(img.kb-zoomable, video, iframe),
.kb-card.expanded .kb-content > p > :is(img.kb-zoomable, video, iframe):only-child {
  display: block; max-width: var(--kb-breakout);
  margin-left: 50%; transform: translateX(-50%);
}
.kb-card.expanded .kb-content > table {
  max-width: var(--kb-breakout);
  margin-left: 50%; transform: translateX(-50%);
}
/* Tables and the 16:9 embeds are sized by width rather than by their content,
   so raising max-width alone would leave them at the measure. */
.kb-card.expanded .kb-content > table,
.kb-card.expanded .kb-content > iframe[src*="youtube"],
.kb-card.expanded .kb-content > iframe[src*="vimeo"],
.kb-card.expanded .kb-content > p > iframe[src*="youtube"]:only-child,
.kb-card.expanded .kb-content > p > iframe[src*="vimeo"]:only-child {
  width: var(--kb-breakout);
}
.kb-article h1 { font-size: 18px; line-height: 24px; margin: 8px 0 4px; font-weight: 600; }
/* "Asset Management · 3 min read" */
.kb-article-meta { margin: 0 0 14px; font-size: 12px; color: var(--kb-faint); }

/* "Was this article helpful?" — sits in .kb-footer, which supplies the rule and
   the padding, so this is just the row itself. */
.kb-vote { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
.kb-vote-q { font-size: 13px; font-weight: 500; color: var(--kb-muted); }
.kb-vote-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px; border-radius: var(--kb-radius); cursor: pointer;
  border: 1px solid var(--kb-border-strong); background: var(--kb-bg);
  font-size: 13px; font-weight: 500; color: var(--kb-ink);
}
.kb-vote-btn:hover { border-color: var(--kb-primary); color: var(--kb-primary-hover); }
.kb-vote-btn svg { width: 14px; height: 14px; }
.kb-content { font-size: 14px; line-height: 1.6; color: var(--kb-ink); }
.kb-content h1, .kb-content h2, .kb-content h3 { line-height: 1.3; margin: 18px 0 8px; font-weight: 600; }
.kb-content h1 { font-size: 18px; }
.kb-content h2 { font-size: 16px; }
.kb-content h3 { font-size: 14px; }
.kb-content p { margin: 0 0 12px; }
.kb-content a { color: var(--kb-primary-hover); }
.kb-content ul, .kb-content ol { margin: 0 0 12px; padding-left: 22px; }
.kb-content li { margin: 4px 0; }
.kb-content img { max-width: 100%; height: auto; border-radius: 8px; }
/* Only big images (tagged in App.tsx once loaded) are click-to-zoom. */
.kb-content img.kb-zoomable { cursor: zoom-in; }
/* Video embeds (YouTube/Vimeo iframes + uploaded <video>) scale to the panel. */
.kb-content iframe { max-width: 100%; border: 0; border-radius: 8px; }
.kb-content iframe[src*="youtube"], .kb-content iframe[src*="vimeo"] {
  width: 100%; height: auto; aspect-ratio: 16 / 9;
}
.kb-content video { max-width: 100%; height: auto; border-radius: 8px; }
.kb-content pre {
  background: var(--kb-bg-soft); padding: 12px; border-radius: 8px;
  overflow-x: auto; font-size: 13px;
}
.kb-content code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px;
}
.kb-content :not(pre) > code {
  background: var(--kb-bg-soft); padding: 1px 5px; border-radius: 4px;
}
.kb-content table { border-collapse: collapse; width: 100%; margin: 0 0 12px; }
.kb-content th, .kb-content td { border: 1px solid var(--kb-border); padding: 6px 10px; text-align: left; }
.kb-content blockquote {
  margin: 0 0 12px; padding: 4px 14px; border-left: 3px solid var(--kb-primary); color: var(--kb-muted);
}

/* Contact-support form (SupportForm.tsx) — the in-panel replacement for the
   link out to the Zendesk request page. */
.kb-support-form { display: flex; flex-direction: column; gap: 14px; padding: 4px 4px 12px; }
.kb-card.expanded .kb-support-form { max-width: 640px; margin: 0 auto; }
/* Feedback for a screenshot dragged over the form. */
.kb-support-form.dropping {
  outline: 2px dashed var(--kb-primary); outline-offset: 6px; border-radius: var(--kb-radius);
}
.kb-support-intro { margin: 0; font-size: 13px; line-height: 1.5; color: var(--kb-muted); }
.kb-support-context {
  margin: 0; padding: 8px 10px; border-radius: var(--kb-radius);
  background: var(--kb-bg-soft); font-size: 13px; color: var(--kb-muted);
}
.kb-support-context strong { color: var(--kb-ink); font-weight: 600; }

.kb-field { display: flex; flex-direction: column; gap: 6px; }
.kb-field-label { font-size: 13px; font-weight: 600; color: var(--kb-ink); }
.kb-field-hint { font-weight: 400; color: var(--kb-faint); }
.kb-field input, .kb-field textarea {
  width: 100%; padding: 9px 12px; border-radius: 4px;
  border: 1px solid var(--kb-border-strong); background: var(--kb-bg);
  font: inherit; font-size: 14px; line-height: 20px; color: var(--kb-ink);
}
.kb-field textarea { resize: vertical; min-height: 96px; }
.kb-field input:focus, .kb-field textarea:focus {
  outline: none; border-color: var(--kb-primary);
}
.kb-field input::placeholder, .kb-field textarea::placeholder { color: var(--kb-faint); }

/* The opt-in "send me a copy" box. Row layout, not .kb-field's column, and the
   accent colour so the tick matches the panel rather than the host page. */
.kb-check { display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--kb-ink); cursor: pointer; }
.kb-check input { width: 15px; height: 15px; margin: 0; accent-color: var(--kb-primary); cursor: pointer; }

/* The real file input stays in the tree (it is what actually opens the picker)
   but is never shown — .kb-attach is the visible control. display:none would
   also work; this keeps it focusable for keyboard users who tab into it. */
.kb-file-input {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip-path: inset(50%); white-space: nowrap; border: 0;
}
.kb-attach {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  align-self: flex-start; padding: 8px 12px; border-radius: var(--kb-radius);
  border: 1px dashed var(--kb-border-strong); background: var(--kb-bg);
  font: inherit; font-size: 13px; font-weight: 500; color: var(--kb-primary-hover);
  cursor: pointer;
}
.kb-attach:hover:not(:disabled) { background: var(--kb-primary-tint); border-color: var(--kb-primary); }
.kb-attach:disabled { color: var(--kb-faint); cursor: default; }
.kb-attach svg { width: 14px; height: 14px; }

.kb-shots { list-style: none; margin: 4px 0 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.kb-shot {
  display: flex; align-items: center; gap: 10px; padding: 6px 8px;
  border: 1px solid var(--kb-border); border-radius: var(--kb-radius);
}
.kb-shot img {
  width: 44px; height: 32px; object-fit: cover; border-radius: 4px;
  background: var(--kb-bg-soft); flex: 0 0 auto;
}
.kb-shot-name {
  flex: 1; min-width: 0; display: flex; flex-direction: column;
  font-size: 12.5px; color: var(--kb-ink);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.kb-shot-size { font-size: 11.5px; color: var(--kb-faint); }
.kb-shot-remove {
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; flex: 0 0 auto;
  border: 0; border-radius: 6px; background: transparent;
  color: var(--kb-faint); cursor: pointer;
}
.kb-shot-remove:hover { background: var(--kb-bg-soft); color: #b42318; }
.kb-shot-remove svg { width: 14px; height: 14px; }

.kb-support-error {
  margin: 0; padding: 8px 10px; border-radius: var(--kb-radius);
  background: #fef3f2; border: 1px solid #fecdca;
  font-size: 13px; line-height: 1.45; color: #b42318;
}

.kb-support-actions { display: flex; gap: 8px; }
.kb-support-cancel, .kb-support-submit {
  flex: 1; padding: 10px 12px; border-radius: var(--kb-radius);
  font: inherit; font-size: 14px; font-weight: 500; cursor: pointer;
}
.kb-support-cancel {
  border: 1px solid var(--kb-border-strong); background: var(--kb-bg); color: var(--kb-muted);
}
.kb-support-cancel:hover:not(:disabled) { background: var(--kb-bg-soft); color: var(--kb-ink); }
.kb-support-submit {
  flex: 2; border: 0; background: var(--kb-primary); color: var(--kb-primary-ink);
  font-weight: 600;
}
.kb-support-submit:hover:not(:disabled) { background: var(--kb-primary-hover); }
.kb-support-submit:disabled, .kb-support-cancel:disabled {
  opacity: .55; cursor: default;
}

/* Sent confirmation — same shape as the empty-search state. */
.kb-support-sent {
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  padding: 48px 18px; text-align: center;
}
.kb-support-sent-glyph {
  display: inline-flex; align-items: center; justify-content: center;
  width: 48px; height: 48px; border-radius: 999px;
  background: var(--kb-primary-tint); color: var(--kb-primary-hover);
}
.kb-support-sent-glyph svg { width: 24px; height: 24px; }
.kb-support-sent-title { margin: 0; font-size: 15px; font-weight: 600; color: var(--kb-ink); }
.kb-support-sent-note {
  margin: 0 0 8px; max-width: 320px; font-size: 13px; line-height: 1.5; color: var(--kb-muted);
}
.kb-support-sent .kb-support-submit { flex: 0 0 auto; padding: 10px 18px; }

/* Lightbox — full-screen image preview above the card */
.kb-lightbox {
  position: fixed; inset: 0; z-index: 2147483004;
  display: flex; align-items: center; justify-content: center;
  padding: 32px; background: rgba(15, 23, 42, 0.82);
  cursor: zoom-out;
}
.kb-lightbox img {
  max-width: 100%; max-height: 100%;
  border-radius: 8px; box-shadow: var(--kb-shadow);
  cursor: default;
}
.kb-lightbox-close {
  position: fixed; top: 16px; right: 16px;
  display: inline-flex; align-items: center; justify-content: center;
  width: 40px; height: 40px; border: 0; border-radius: 999px; cursor: pointer;
  background: rgba(255, 255, 255, 0.14); color: #ffffff;
}
.kb-lightbox-close:hover { background: rgba(255, 255, 255, 0.28); }
.kb-lightbox-close svg { width: 20px; height: 20px; }

@media (max-width: 480px) {
  .kb-card, .kb-card.expanded { width: calc(100vw - 24px); height: calc(100vh - 24px); bottom: 12px; }
  /* After the full-height rule above, or a collapsed panel would still fill the
     screen with an empty card under its header bar. */
  .kb-card.collapsed { height: auto; }
  .kb-card.side-right { right: 12px; }
  .kb-card.side-left { left: 12px; }
  .kb-lightbox { padding: 16px; }
}
`;
}
